import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adminLogin, adminMe, adminListInquiries, adminGetInquiry,
  adminSetStatus, adminDownloadFile, clearToken, getToken, formatApiError,
} from "@/lib/api";
import { Seo } from "@/components/Seo";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso || "—"; }
};
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const inputCls =
  "w-full bg-transparent border border-[rgba(180,139,63,0.35)] px-4 py-3 text-[var(--bone)] font-serif2 text-lg placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--gold)] transition-colors";

function LoginView({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await adminLogin(email.trim(), password);
      onAuthed(user);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Sign-in failed."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen glow-navy flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="founder-login-form">
        <p className="text-center font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Steward &amp; Stone</p>
        <h1 className="mt-3 text-center font-serif2 text-3xl text-[var(--bone)]">Founder Inbox</h1>
        <p className="mt-3 text-center text-sm text-[var(--muted)]">Private assessment review. Authorized access only.</p>
        <div className="mt-10 flex flex-col gap-4">
          <input className={inputCls} type="email" aria-label="Email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="founder-login-email" />
          <input className={inputCls} type="password" aria-label="Password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="founder-login-password" />
          <button disabled={loading} className="jmh-cta mt-2 disabled:opacity-50" data-testid="founder-login-submit">
            {loading ? "Signing in…" : "Enter"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Detail({ id, onBack, onChanged }) {
  const [item, setItem] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminGetInquiry(id).then(setItem).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [id]);

  const toggle = async () => {
    setBusy(true);
    try {
      const next = item.status === "reviewed" ? "new" : "reviewed";
      const updated = await adminSetStatus(id, next);
      setItem(updated);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const download = async (att) => {
    try {
      const blob = await adminDownloadFile(att.file_id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not open document."));
    }
  };

  if (!item) return <div className="text-[var(--muted)] py-20 text-center">Loading…</div>;

  return (
    <div data-testid="founder-inquiry-detail">
      <button onClick={onBack} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="founder-detail-back">← All inquiries</button>
      <div className="corner-frame mt-6 px-6 md:px-8 py-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <span className={`font-display uppercase tracking-wide2 text-[10px] px-3 py-1 border ${item.status === "reviewed" ? "border-[rgba(180,139,63,0.3)] text-[var(--muted)]" : "border-[var(--gold)] text-[var(--gold-lite)]"}`}>
            {item.status === "reviewed" ? "Reviewed" : "New"}
          </span>
          <span className="text-xs text-[var(--muted)]">{fmtDate(item.created_at)}</span>
        </div>

        <h2 className="mt-6 font-serif2 text-2xl text-[var(--bone)]">{item.review_type || (item.source === "jmh_contact" ? "JMH Contact Inquiry" : "Assessment Inquiry")}</h2>

        <dl className="mt-6 space-y-4 text-[var(--bone)]/90">
          <Field label="Email"><a className="gold-text" href={`mailto:${item.email}`}>{item.email}</a></Field>
          {item.phone && <Field label="Phone">{item.phone}</Field>}
          {item.name && <Field label="Name">{item.name}</Field>}
          <Field label="Division">{item.division || "—"}</Field>
          <Field label="Narrative / Context"><p className="whitespace-pre-wrap leading-relaxed font-serif2 text-lg">{item.message || "—"}</p></Field>
        </dl>

        <div className="mt-8">
          <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-3">Supporting Documents</p>
          {item.attachments && item.attachments.length ? (
            <ul className="space-y-2" data-testid="founder-detail-attachments">
              {item.attachments.map((a) => (
                <li key={a.file_id}>
                  <button onClick={() => download(a)} className="w-full text-left flex items-center justify-between gap-4 border border-[rgba(180,139,63,0.28)] px-4 py-3 hover:border-[var(--gold)] transition-colors" data-testid={`founder-attachment-${a.file_id}`}>
                    <span className="text-[var(--bone)]/90 truncate">{a.original_filename}</span>
                    <span className="text-[10px] uppercase tracking-wide2 text-[var(--gold-lite)] shrink-0">{fmtSize(a.size)} · Open</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-[var(--muted)]">No documents were attached.</p>
          )}
        </div>

        <div className="mt-10">
          <button onClick={toggle} disabled={busy} className="jmh-cta disabled:opacity-50" data-testid="founder-detail-toggle-status">
            {item.status === "reviewed" ? "Mark as New" : "Mark as Reviewed"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <dt className="font-display uppercase tracking-wide2 text-[10px] text-[var(--muted)]">{label}</dt>
    <dd className="mt-1">{children}</dd>
  </div>
);

export default function FounderInbox() {
  const [authed, setAuthed] = useState(null); // null = checking
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!getToken()) { setAuthed(false); return; }
    adminMe().then(() => setAuthed(true)).catch(() => { clearToken(); setAuthed(false); });
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await adminListInquiries(""));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authed) load(); }, [authed]);

  if (authed === null) return <div className="min-h-screen glow-navy" />;
  if (!authed) return <LoginView onAuthed={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen glow-navy px-6 py-12">
      <Seo title="Steward & Stone — Founder Inbox" />
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Steward &amp; Stone</p>
            <h1 className="mt-1 font-serif2 text-3xl text-[var(--bone)]">Founder Inbox</h1>
          </div>
          <button onClick={() => { clearToken(); setAuthed(false); }} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="founder-logout">Sign out</button>
        </div>

        <div className="mt-10">
          {selected ? (
            <Detail id={selected} onBack={() => setSelected(null)} onChanged={load} />
          ) : loading ? (
            <div className="text-[var(--muted)] py-20 text-center">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-[var(--muted)] py-20 text-center" data-testid="founder-inbox-empty">No inquiries yet.</div>
          ) : (
            <ul className="space-y-3" data-testid="founder-inbox-list">
              {items.map((it) => (
                <li key={it.id}>
                  <button onClick={() => setSelected(it.id)} className="w-full text-left border border-[rgba(180,139,63,0.22)] px-5 py-4 hover:border-[var(--gold)] transition-colors flex items-center justify-between gap-4" data-testid={`founder-inbox-item-${it.id}`}>
                    <div className="min-w-0">
                      <p className="text-[var(--bone)] font-serif2 text-lg truncate">{it.review_type || (it.source === "jmh_contact" ? "JMH Contact Inquiry" : "Assessment Inquiry")}</p>
                      <p className="text-xs text-[var(--muted)] truncate">{it.email} · {fmtDate(it.created_at)}{it.attachments?.length ? ` · ${it.attachments.length} doc(s)` : ""}</p>
                    </div>
                    <span className={`shrink-0 font-display uppercase tracking-wide2 text-[9px] px-2.5 py-1 border ${it.status === "reviewed" ? "border-[rgba(180,139,63,0.3)] text-[var(--muted)]" : "border-[var(--gold)] text-[var(--gold-lite)]"}`}>
                      {it.status === "reviewed" ? "Reviewed" : "New"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
